import { FC } from 'react'
import { format, getSeconds } from 'date-fns'
import { Duration } from 'luxon'
import styled from 'styled-components'

import {
  chain,
  ExpandableCell,
  Stepper,
  StepperStep,
  StepperStepState,
  Token,
} from '@revolut-internal/ui-kit'

import {
  AgentAttendanceShiftStatusTimelineSegmentsInner,
  AgentAttendanceShiftStatusTimelineSegmentsInnerTimelinesInner,
} from 'types'
import { formatNumber } from 'utils/formatNumber'
import { formatDuration } from 'utils/formatTimeAndDuration'

type ShiftTimelineSegmentProps = {
  timelineSegment: AgentAttendanceShiftStatusTimelineSegmentsInner
  adherence?: number
  isFirst?: boolean
  showDates?: boolean
}

const StyledStepper = styled(Stepper)`
  background-color: ${Token.color.greyTone5};
  padding: ${Token.size.s4} ${Token.size.s8} 0;
  border-radius: ${Token.radius.r16};
`
const StyledStepperStep = styled(StepperStep)`
  // TODO: remove it once UI-kit is updated
  //  there is no other way to fix a bug with truncated step as to override
  //  we need to add (0, 4, 0) selector which doesn't make sense
  clip-path: none !important;
`

const getStepStatus = (
  timelines: AgentAttendanceShiftStatusTimelineSegmentsInnerTimelinesInner[],
  index: number,
) => {
  const timeline = timelines[index]
  const isInFuture = timeline.startDate > Date.now()
  const isLast = !timelines[index + 1]
  const isAfterAway = timelines[index - 1]?.isAway

  if (timeline.isAway || isInFuture || (isLast && isAfterAway)) {
    return StepperStepState.DEFAULT
  }
  return StepperStepState.PAST
}

export const ShiftTimelineSegment: FC<ShiftTimelineSegmentProps> = ({
  adherence,
  timelineSegment,
  isFirst = false,
  showDates = false,
}) => {
  const currentTimestamp = Date.now()
  const isActive =
    currentTimestamp >= timelineSegment.startDate &&
    currentTimestamp < timelineSegment.endDate
  const duration = formatDuration(
    Duration.fromMillis(timelineSegment.endDate - timelineSegment.startDate),
  )

  return (
    <ExpandableCell prefersExpanded>
      <ExpandableCell.Title color={isActive ? 'accent' : undefined}>
        {timelineSegment.name} (
        {chain(
          duration,
          timelineSegment.adherence
            ? `${formatNumber(Math.round(timelineSegment.adherence))}%`
            : undefined,
        )}
        )
      </ExpandableCell.Title>
      {!!(isFirst && adherence) && (
        <ExpandableCell.Content>
          {formatNumber(Math.round(adherence))}% adherence
        </ExpandableCell.Content>
      )}
      <ExpandableCell.Note>
        <StyledStepper orientation="vertical">
          {timelineSegment.timelines.map((timeline, index) => {
            const secondsFormatter = getSeconds(timeline.startDate) ? ':ss' : ''
            const dayFormatter = (isFirst && index === 0) || showDates ? ' MMM do' : ''

            return (
              <StyledStepperStep
                key={`${timeline.startDate}-${index}`}
                state={getStepStatus(timelineSegment.timelines, index)}
              >
                <StepperStep.Title>{timeline.title}</StepperStep.Title>
                <StepperStep.Description>
                  {chain(
                    format(timeline.startDate, `HH:mm${secondsFormatter}${dayFormatter}`),
                    !timeline.startsIn &&
                      timeline.duration &&
                      formatDuration(timeline.duration),
                    timeline.startsIn && `in ${formatDuration(timeline.startsIn)}`,
                    timeline.previousState,
                  )}
                </StepperStep.Description>
              </StyledStepperStep>
            )
          })}
        </StyledStepper>
      </ExpandableCell.Note>
    </ExpandableCell>
  )
}
